"use client";

import { useEffect, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { BrainIcon, FilterIcon, GlobeIcon, InfoIcon, LockIcon, RefreshCwIcon, XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { McpServerIcon } from "@/lib/mcpServerIcons";
import { useMcpServers } from "@/components/custom/contexts/mcpServersContext";
import { useMcpServerDateFilters } from "@/components/custom/contexts/mcpServerDateFiltersContext";
import { useExtendedThinking } from "@/components/custom/contexts/extendedThinkingContext";
import { useWebSearch } from "@/components/custom/contexts/webSearchContext";
import { useWebSearchDomains } from "@/components/custom/contexts/webSearchDomainsContext";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { SegmentedDateInput } from "./SegmentedDateInput";

// ISO date string (YYYY-MM-DD) → locale-formatted display ("19. 5. 2026" cs,
// "5/19/2026" en, etc.). Returns the raw string if Date parsing fails so a
// bad value doesn't blank the chip.
function formatLocaleDate(iso: string): string {
  const parts = iso.split("-").map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return iso;
  const d = new Date(Date.UTC(parts[0]!, parts[1]! - 1, parts[2]!));
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(i18n.language, { timeZone: "UTC" }).format(d);
  } catch {
    return iso;
  }
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Pill sizing matches CHIP_BASE in ComposerScopeIndicator so Thinking / Web / MCP
// chips visually align as a single row of equally-sized chips next to the scope
// indicator. Previous larger sizing (px-4 py-1.5 text-sm) made the scope chip
// look subordinate; this matches it 1:1.
const PILL_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors select-none shrink-0 cursor-pointer";
const PILL_ACTIVE = "border-blue-400 bg-blue-50 text-blue-700";
const PILL_INACTIVE =
  "border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground";

export const ExtendedThinkingToggle: FC = () => {
  const { t } = useTranslation();
  const { extendedThinking, toggleExtendedThinking } = useExtendedThinking();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={toggleExtendedThinking}
          className={cn(PILL_BASE, extendedThinking ? PILL_ACTIVE : PILL_INACTIVE)}
        >
          <BrainIcon className="size-4 shrink-0" />
          <span className="max-w-[10rem] truncate">{t("composer.thinking")}</span>
        </button>
      </TooltipTrigger>
    </Tooltip>
  );
};

// ─── WebChip ─────────────────────────────────────────────────────────────────
// Shown in the action bar when web search is enabled. A FilterIcon button opens
// a dropdown with the single "Limit to recommended sources" tickbox; the
// InfoIcon inside the dropdown reveals the (currently empty) admin-configured
// allowlist on hover. X disables web search entirely.
//
// Gated visual state: when the tickbox is ON and the recommended list is empty,
// the chip renders muted with a lock badge — web search is effectively off for
// this turn (backend receives `webSearchAllowedDomains: []` → no allowed sources
// → no results). User can either uncheck the box or wait for the admin to
// populate the list.

export const WebChip: FC = () => {
  const { t } = useTranslation();
  const { webSearch, toggleWebSearch } = useWebSearch();
  const { limitToRecommended, setLimitToRecommended, recommendedSources } = useWebSearchDomains();

  if (!webSearch) return null;

  const isGated = limitToRecommended && recommendedSources.length === 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            PILL_BASE,
            isGated ? PILL_INACTIVE : PILL_ACTIVE,
            isGated && "opacity-80",
          )}
          title={isGated ? t("composer.web.gatedTooltip") : undefined}
        >
          <span className="relative">
            <GlobeIcon className="size-4 shrink-0" />
            {isGated && (
              <span className="absolute -bottom-0.5 -right-0.5 inline-flex size-[10px] items-center justify-center rounded-full border border-background bg-amber-100 text-amber-700">
                <LockIcon className="size-[7px]" />
              </span>
            )}
          </span>
          <span className="max-w-[10rem] truncate">{t("composer.web")}</span>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                title={t("composer.web.limitToRecommended")}
                aria-label={t("composer.web.limitToRecommended")}
                className={cn(
                  "ml-0.5 rounded transition-colors cursor-pointer",
                  isGated ? "text-muted-foreground hover:text-foreground" : "text-blue-500 hover:text-blue-800",
                )}
              >
                <FilterIcon className="size-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" sideOffset={6} className="w-72">
              <DropdownMenuCheckboxItem
                checked={limitToRecommended}
                onCheckedChange={(value) => setLimitToRecommended(Boolean(value))}
                onSelect={(e) => e.preventDefault()}
                className="cursor-pointer"
              >
                <span className="flex-1">{t("composer.web.limitToRecommended")}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-2 text-muted-foreground">
                      <InfoIcon className="size-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="start" className="max-w-xs">
                    <div className="text-[11px] font-medium text-muted-foreground uppercase mb-1">
                      {t("composer.web.recommendedSourcesHeader")}
                    </div>
                    {recommendedSources.length === 0 ? (
                      <div className="text-[12px] text-muted-foreground italic">
                        {t("composer.web.recommendedSourcesEmpty")}
                      </div>
                    ) : (
                      <ul className="text-[12px] text-foreground/80 space-y-0.5">
                        {recommendedSources.map(src => (
                          <li key={src} className="truncate">{src}</li>
                        ))}
                      </ul>
                    )}
                  </TooltipContent>
                </Tooltip>
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            type="button"
            onClick={toggleWebSearch}
            title={t("composer.removeSource", { name: t("composer.web") })}
            className={cn(
              "ml-0.5 rounded transition-colors cursor-pointer",
              isGated ? "text-muted-foreground hover:text-foreground" : "text-blue-500 hover:text-blue-800",
            )}
          >
            <XIcon className="size-3" />
          </button>
        </span>
      </TooltipTrigger>
      {isGated && (
        <TooltipContent side="top">{t("composer.web.gatedTooltip")}</TooltipContent>
      )}
    </Tooltip>
  );
};

// ─── McpServerChip ───────────────────────────────────────────────────────────
// Per-server pill in the action bar. Health dot, retry on error, X to remove
// (hidden for required servers).

export const McpServerChip: FC<{ name: string }> = ({ name }) => {
  const { t } = useTranslation();
  const { allServers, serverHealth, toggleServer, validateServers, openSourceUnavailable } = useMcpServers();
  const { dateFilters, setDateFilter, clearDateFilter } = useMcpServerDateFilters();
  const server = allServers.find(s => s.name === name);
  const canRemove = !server?.required;
  const health = serverHealth[name];
  const isPaywalled = health?.status === "paywalled";
  const supportsDateFilter = server?.dateFilter === true;
  const activeDate = dateFilters[name];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(PILL_BASE, PILL_ACTIVE, isPaywalled && "opacity-70")}
          onClick={() => {
            if (isPaywalled) openSourceUnavailable(name, "paywalled");
          }}
        >
          <span className="relative">
            <McpServerIcon name={name} />
            {health && health.status !== "paywalled" && (
              <span
                title={
                  health.status === "error"
                    ? health.error
                    : health.status === "healthy"
                      ? `${health.toolCount ?? 0} tools`
                      : undefined
                }
                className={cn(
                  "absolute -bottom-0.5 -right-0.5 size-2 rounded-full border border-background",
                  health.status === "validating" && "bg-amber-400 animate-pulse",
                  health.status === "healthy" && "bg-emerald-500",
                  health.status === "error" && "bg-red-500",
                )}
              />
            )}
            {isPaywalled && (
              <span
                title={t("composer.mcp.paywalled.badgeTooltip")}
                className="absolute -bottom-0.5 -right-0.5 inline-flex size-[10px] items-center justify-center rounded-full border border-background bg-amber-100 text-amber-700"
              >
                <LockIcon className="size-[7px]" />
              </span>
            )}
          </span>
          <span className="max-w-[10rem] truncate">{t(`${name}.label`)}</span>

          {/* Active date badge — only when filter is set and server supports it. */}
          {supportsDateFilter && activeDate && (
            <span
              className="ml-0.5 rounded-full bg-blue-200/60 px-1.5 py-0.5 text-[10px] tabular-nums leading-none"
              title={t("composer.mcp.dateFilter.activeTooltip", { date: formatLocaleDate(activeDate) })}
            >
              {formatLocaleDate(activeDate)}
            </span>
          )}

          {/* Date filter popover — only when supported and not paywalled. */}
          {supportsDateFilter && !isPaywalled && (
            <McpDateFilterPopover
              name={name}
              currentDate={activeDate}
              onApply={(d) => setDateFilter(name, d)}
              onClear={() => clearDateFilter(name)}
            />
          )}

          {health?.status === "error" && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                validateServers([name]);
              }}
              title={t("composer.retryConnection", { name: t(`${name}.label`) })}
              className="ml-0.5 rounded text-blue-500 hover:text-blue-800 transition-colors cursor-pointer"
            >
              <RefreshCwIcon className="size-3" />
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleServer(name);
              }}
              title={t("composer.removeSource", { name: t(`${name}.label`) })}
              className="ml-0.5 rounded text-blue-500 hover:text-blue-800 transition-colors cursor-pointer"
            >
              <XIcon className="size-3" />
            </button>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        {isPaywalled ? t("composer.mcp.paywalled.badgeTooltip") : t(`${name}.toolGroupHeading`)}
      </TooltipContent>
    </Tooltip>
  );
};

// ─── McpDateFilterPopover ────────────────────────────────────────────────────
// Compact popover hung off the chip's FilterIcon. Uses a segmented date input
// (YYYY-MM-DD, auto-advancing between fields) for ergonomic editing. Prefills
// with today's date when no value is set.

const McpDateFilterPopover: FC<{
  name: string;
  currentDate: string | undefined;
  onApply: (iso: string) => void;
  onClear: () => void;
}> = ({ name, currentDate, onApply, onClear }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(currentDate ?? "");

  // Re-sync draft when the popover opens. Prefill with today if no active date.
  useEffect(() => {
    if (open) {
      if (currentDate) {
        setDraft(currentDate);
      } else {
        const now = new Date();
        const y = String(now.getFullYear());
        const m = String(now.getMonth() + 1).padStart(2, "0");
        const d = String(now.getDate()).padStart(2, "0");
        setDraft(`${y}-${m}-${d}`);
      }
    }
  }, [open, currentDate]);

  const submit = () => {
    if (!ISO_DATE_RE.test(draft)) return;
    onApply(draft);
    setOpen(false);
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          title={t("composer.mcp.dateFilter.label", { name: t(`${name}.label`) })}
          aria-label={t("composer.mcp.dateFilter.label", { name: t(`${name}.label`) })}
          className="ml-0.5 rounded text-blue-500 hover:text-blue-800 transition-colors cursor-pointer"
        >
          <FilterIcon className="size-3" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="top"
          align="start"
          sideOffset={6}
          onClick={(e) => e.stopPropagation()}
          className="z-50 rounded-xl border border-border bg-popover p-3 shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
            {t("composer.mcp.dateFilter.label", { name: t(`${name}.label`) })}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); submit(); }}
            className="flex flex-col gap-2"
          >
            <SegmentedDateInput
              value={draft}
              onChange={setDraft}
              onSubmit={submit}
              autoFocus
            />
            <div className="flex items-center justify-end gap-2">
              {currentDate && (
                <button
                  type="button"
                  onClick={() => { onClear(); setDraft(""); setOpen(false); }}
                  className="rounded-md border border-border px-2 py-1 text-xs font-medium bg-foreground/5 hover:bg-foreground/10 cursor-pointer"
                >
                  {t("composer.mcp.dateFilter.clear")}
                </button>
              )}
              <button
                type="submit"
                disabled={!ISO_DATE_RE.test(draft)}
                className={cn(
                  "rounded-md border px-2 py-1 text-xs font-medium transition-colors",
                  ISO_DATE_RE.test(draft)
                    ? "border-blue-400 bg-blue-50 text-blue-700 hover:bg-blue-100 cursor-pointer"
                    : "border-border opacity-50 cursor-not-allowed",
                )}
              >
                {t("composer.mcp.dateFilter.apply")}
              </button>
            </div>
          </form>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};

